/**
 * TradingView Datafeed for Equity / Sector Charting
 * Routes through Kaze backend proxy for OHLCV data via Yahoo Finance.
 */

import api from '../services/api';

interface Bar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export function createEquityDatafeed() {
  const subscribers = new Map<string, { interval?: ReturnType<typeof setInterval> }>();

  return {
    onReady: (callback: (config: unknown) => void) => {
      setTimeout(() => {
        callback({
          supported_resolutions: ['D', 'W', 'M'],
          exchanges: [{ value: 'US', name: 'US Equities', desc: 'US Stock Market' }],
          symbols_types: [{ name: 'stock', value: 'stock' }],
        });
      }, 0);
    },

    searchSymbols: (
      _userInput: string,
      _exchange: string,
      _symbolType: string,
      onResult: (result: unknown[]) => void,
    ) => {
      onResult([]);
    },

    resolveSymbol: (
      symbolName: string,
      onResolved: (info: unknown) => void,
      _onError: (reason: string) => void,
    ) => {
      const ticker = symbolName.replace('US:', '');
      setTimeout(
        () =>
          onResolved({
            name: symbolName,
            ticker,
            description: ticker,
            type: 'stock',
            session: '0930-1600',
            timezone: 'America/New_York',
            exchange: 'US',
            minmov: 1,
            pricescale: 100,
            has_intraday: false,
            has_daily: true,
            has_weekly_and_monthly: true,
            supported_resolutions: ['D', 'W', 'M'],
            volume_precision: 0,
            data_status: 'delayed',
          }),
        0,
      );
    },

    getBars: async (
      symbolInfo: { name: string; ticker?: string },
      _resolution: string,
      periodParams: { from: number; to: number },
      onHistory: (bars: Bar[], meta: { noData: boolean }) => void,
    ) => {
      try {
        const ticker = symbolInfo.ticker || symbolInfo.name.replace('US:', '');
        const res = await api.get(
          `/kaze/chart-data?ticker=${encodeURIComponent(ticker)}&from=${periodParams.from}&to=${periodParams.to}`,
        );
        const candles = res.data?.data?.candles;
        if (!candles?.length) {
          onHistory([], { noData: true });
          return;
        }
        const bars: Bar[] = candles
          .map((c: { time: number; open: number; high: number; low: number; close: number; volume: number }) => ({
            time: c.time * 1000,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
            volume: c.volume,
          }))
          .sort((a: Bar, b: Bar) => a.time - b.time);
        onHistory(bars, { noData: false });
      } catch {
        onHistory([], { noData: true });
      }
    },

    subscribeBars: (
      _symbolInfo: { name: string },
      _resolution: string,
      _onRealtime: (bar: Bar) => void,
      subscriberUID: string,
    ) => {
      // Equities don't need real-time streaming; placeholder
      subscribers.set(subscriberUID, {});
    },

    unsubscribeBars: (uid: string) => {
      const sub = subscribers.get(uid);
      if (sub?.interval) clearInterval(sub.interval);
      subscribers.delete(uid);
    },
  };
}
